import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { CompassLogo } from "../components/CompassLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, MailQuestion } from "lucide-react";

/**
 * Issue #410: this page used to render a `reset_link` straight out of the
 * response — the prototype API returned one as a no-email-provider
 * workaround. The real endpoint deliberately never does (a reset link in
 * the response body hands any visitor who can type an email address that
 * account's credentials), so the page now shows only the server's generic
 * confirmation, which is the same whether or not the email is registered.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setResult(data);
      toast.success("Request sent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Request failed");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg px-3 py-2 inline-flex mb-6">
          <CompassLogo size={44} />
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4" style={{ background: "#0B2E5915" }}>
            <MailQuestion size={22} color="#0B2E59" />
          </div>
          <h2 className="font-display font-black text-2xl text-slate-900">Reset your password</h2>
          <p className="text-slate-500 mt-1 text-sm">Enter the email tied to your CricketCompass account and we'll email you a one-hour reset link.</p>

          {!result && (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label>Email</Label>
                <Input data-testid="fp-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 h-11" />
              </div>
              <Button data-testid="fp-submit" type="submit" disabled={busy} className="w-full h-11" style={{ background: "#0B2E59" }}>
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          )}

          {result && (
            <div className="mt-6 space-y-4" data-testid="fp-result">
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-sm text-slate-700">{result.message}</p>
                <p className="text-xs text-slate-500 mt-2">
                  If it doesn't arrive within a few minutes, check your spam folder before requesting another.
                </p>
              </div>
              <Button variant="outline" className="w-full" onClick={() => { setResult(null); setEmail(""); }}>Send another</Button>
            </div>
          )}

          <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900" data-testid="fp-back">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
